import * as React from 'react';
import { useIntl } from 'react-intl';
import { CmdChipSelect, CmdModal, CmdModalFooterButton } from '@commander-services/gui-components';
import dayjs from 'dayjs';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useQueries } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import FormikValidatorService, {
  IValidationApiResponse,
} from '../../../Services/FormikValidatorService';
import Label from '../../Forms/Fields/Label';
import usePageWithForm from '../../../hooks/usePageWithForm';
import {
  ICheckedTableItems,
  IFilterTripsOverview,
  IInfoRideTag,
  ITagItem,
  ITripOverviewParking,
  ITripsOverViewRecord,
  ITripsOverviewAdvancedFilterForRequest,
  ITripsOverviewInfo,
} from '../interfaces';
import { IS_USAGE, IS_USAGE_PARKING } from '../constants';
import { transformErrors } from '../../../Services/HookFormService';
import { selectedVehiclesAtom } from '../../../store/recoil/vehicles';
import {
  advancedFiltersAtom,
  checkedTableItemsAtom,
  filterForRequestAtom,
  isActiveAdvanceFilterAtom,
  parkingUsageAtom,
  rideItemAtom,
  rideUsageAtom,
} from '../TripsOverviewState';
import {
  addUsage,
  convertTimeToSeconds,
  getTagsData,
  handleFilterKey,
} from '../TripsOverviewService';
import useAnalytics from '../../../hooks/useAnalytics';
import { GDPR_VALUE } from "../../CmdTable/constants.ts";

interface IAddUsageProps {
  closeCallback: () => void;
  type: string;
  data: ITripsOverViewRecord | null;
  updateTable: () => void;
}

function AddUsage(props: IAddUsageProps): JSX.Element {
  usePageWithForm();
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  const filterForRequest = useRecoilValue<IFilterTripsOverview>(filterForRequestAtom);
  const { formatMessage: f } = useIntl();
  const setRideItem = useSetRecoilState(rideItemAtom(props.data ? String(props.data.id) : ''));
  const setRideUsage = useSetRecoilState(rideUsageAtom);
  const setParkingUsage = useSetRecoilState(parkingUsageAtom);
  const checkedTableItems = useRecoilValue<ICheckedTableItems>(checkedTableItemsAtom);
  const [isLoaderActive, setIsLoaderActive] = React.useState<boolean>(false);
  const [isLongStart, setIsLongStart] = React.useState<boolean>(false);
  const [isLongEnd, setIsLongEnd] = React.useState<boolean>(false);
  const [isLongVehicle, setIsLongVehicle] = React.useState<boolean>(false);
  const [options, setOptions] = React.useState<ITagItem[] | IInfoRideTag[]>([]);
  const isActiveAdvanceFilter = useRecoilValue(isActiveAdvanceFilterAtom);
  const advancedFilterValues = useRecoilValue(advancedFiltersAtom);
  const { trackEvent } = useAnalytics();

  const validateFields = (values) => {
    let formErrors = {};
    const validation: IValidationApiResponse = {
      tagId: [{ param: 'required', value: true, message: f({ id: 'form.required' }) }],
    };
    formErrors = FormikValidatorService.validateByValidationApiResponse(values, validation);
    return transformErrors(formErrors);
  };

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<{ tagId: string }>({
    mode: 'onChange',
    defaultValues: {
      tagId: '',
    },
    resolver: async (data) => {
      const formErrors = validateFields(data);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const getTags = async () => {
    const customerId = props.data?.customer?.id || checkedTableItems.customerId;
    if (customerId) {
      const tagOptions = await getTagsData(props.type, String(customerId));
      if (tagOptions) {
        const tags = Object.values(tagOptions).map((i) => {
          i.value = String(i.value);
          i.id = i.value;
          i.name = i.item;
          return i;
        });
        setOptions(tags);
      }
    }
  };

  useQueries({
    queries: [
      {
        queryKey: ['tripsOverviewUsage'],
        queryFn: () => getTags(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const getNotes = (tagIds: string[], oldTagIds: string[]) => {
    return Object.values(options).filter(
      (i) => tagIds.includes(i.value) && !oldTagIds.includes(i.value)
    );
  };

  const onSubmit = async (values) => {
    trackEvent(['ride book', 'click', `Ride book add usage: ${props.type}`]);
    setIsLoaderActive(true);
    const advancedFilters: ITripsOverviewAdvancedFilterForRequest = handleFilterKey(
      {
        ...advancedFilterValues,
        timeFrom: convertTimeToSeconds(advancedFilterValues.timeFrom),
        timeTo: convertTimeToSeconds(advancedFilterValues.timeTo),
      },
      isActiveAdvanceFilter
    );
    const filter = {
      vehicle: selectedVehicles,
      ...advancedFilters,
      ...filterForRequest,
    };

    const res = await addUsage(values, props.data, checkedTableItems, props.type, filter);
    setIsLoaderActive(false);
    if (res) {
      props.closeCallback();
      if (isActiveAdvanceFilter.ridebookPurpose) {
        props.updateTable();
      } else if (props.data) {
        setRideItem((oldState) => {
          if (oldState) {
            const newState: ITripsOverViewRecord = { ...oldState };
            const infoData: ITripsOverviewInfo = { ...oldState.info };
            if (props.type === IS_USAGE_PARKING && oldState.parking) {
              const parkingData: ITripOverviewParking = { ...oldState.parking };
              const oldTagIds = infoData.parkingTags.map((i) => String(i.id));
              const newTags = getNotes(values.tagId, oldTagIds);
              infoData.parkingTags = [...infoData.parkingTags, ...newTags];
              parkingData.summary = {
                ...parkingData.summary,
                tag: parkingData.summary?.tag
                  ? Number(parkingData.summary.tag) + newTags.length
                  : newTags.length,
              };
              newState.parking = { ...parkingData };
            } else {
              const oldTagIds = infoData.rideTags.map((i) => String(i.id));
              const newTags = getNotes(values.tagId, oldTagIds);
              infoData.rideTags = [...infoData.rideTags, ...newTags];
              infoData.summary = {
                ...infoData.summary,
                tag: infoData.summary.tag
                  ? Number(infoData.summary.tag) + newTags.length
                  : newTags.length,
              };
            }
            newState.info = { ...infoData };
            return newState;
          }
          return null;
        });
      } else {
        const items = Object.values(options).filter((i) => values.tagId.includes(i.value));
        if (props.type === IS_USAGE) {
          setRideUsage(items);
        } else {
          setParkingUsage(items);
        }
      }
    }
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'form.cancel' }),
      tooltip: f({ id: 'rides.modal.usage.cancel.tooltip' }),
      closeCallback: props.closeCallback,
    },
    {
      id: 'addusage-form-submit',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'tripsOverview.modal.toAddUsage' }),
      tooltip:
        props.type === IS_USAGE
          ? f({ id: 'rides.modal.usage.submit.tooltip' })
          : f({ id: 'rides.modal.parkingUsage.submit.tooltip' }),
      submitCallback: handleSubmit(onSubmit),
    },
  ];

  const getModalTitle = () => {
    if (props.type === IS_USAGE) {
      return props.data
        ? f({ id: 'tripsOverview.modal.usageAdding' })
        : f({ id: 'tripsOverview.modal.usagesAdding' });
    }
    return props.data
      ? f({ id: 'rides.usageParkingAdding' })
      : f({ id: 'rides.usageParkingsAdding' });
  };

  React.useEffect(() => {
    if (props.data) {
      const startId = document.getElementById(`ride-start-${props.data.timestamp.start}`);
      if (startId && !isLongStart && startId.clientHeight > 19) {
        setIsLongStart(true);
      }
      const endId = document.getElementById(`ride-end-${props.data.timestamp.end}`);
      if (endId && !isLongEnd && endId.clientHeight > 19) {
        setIsLongEnd(true);
      }
      const vehicleId = document.getElementById(`vehicle-${props.data.vehicle.id}`);
      if (vehicleId && !isLongVehicle && vehicleId.clientHeight > 19) {
        setIsLongVehicle(true);
      }
    }
  }, [isLongEnd, isLongStart, isLongVehicle, props.data]);

  const getStartAddress = () => {
    const anonymized = f({ id: 'tables.tripsOverview.column.datetime.anonymized' });
    if (props.type === IS_USAGE) {
      return !props.data?.address.startLink ? anonymized : props.data?.address.start;
    }
    if (props.type !== IS_USAGE) {
      return !props.data?.parking?.address.startLink
        ? anonymized
        : props.data?.parking?.address.start;
    }
    return '';
  };

  const getStartTimestamp = () => {
    if (props.data && props.data.timestamp.start && props.type === IS_USAGE) {
      return dayjs(props.data.timestamp.start).format('DD.MM.YYYY HH:mm:ss');
    }
    if (
      props.data &&
      props.data.parking &&
      props.data.parking.timestamp.start &&
      props.type !== IS_USAGE
    ) {
      return dayjs(props.data.parking.timestamp.start).format('DD.MM.YYYY HH:mm:ss');
    }
    return '';
  };

  const getEndAddress = () => {
    if (props.type === IS_USAGE && props.data?.address.endLink) {
      return props.data.address.end;
    }
    if (props.type !== IS_USAGE && props.data?.parking?.address.endLink) {
      return props.data.parking.address.end;
    }
    return f({ id: 'tables.tripsOverview.column.datetime.anonymized' });
  };

  const getIsTimestampAnonymized = () => {
    if (props.type === IS_USAGE && props.data?.timestamp.end === GDPR_VALUE) {
      return true;
    }
    if (props.type !== IS_USAGE && props.data?.parking?.timestamp.end === GDPR_VALUE) {
      return true;
    }
    return false;
  };

  const getEndValue = () => {
    const address = getEndAddress();
    if (getIsTimestampAnonymized()) {
      return (
        <>
          <i className="cmd-icons cmd-icon-gdpr-document-lock-dark-gray" /> - {address}
        </>
      );
    }
    if (props.type === IS_USAGE) {
      if (props.data?.timestamp.end) {
        return `${dayjs(props.data?.timestamp.end).format('DD.MM.YYYY HH:mm:ss')} - ${address}`;
      }
    } else if (props.data?.parking?.timestamp.end) {
      return `${dayjs(props.data?.parking?.timestamp.end).format(
        'DD.MM.YYYY HH:mm:ss'
      )} - ${address}`;
    }
    return '';
  };

  return (
    <CmdModal
      id="usage-form-modal"
      title={getModalTitle()}
      footerButtons={buttons}
      loader={isLoaderActive}
    >
      {props.data && (
        <div style={{ marginBottom: '24px' }}>
          <Label
            title={f({ id: 'tripsOverview.modal.vehicle' })}
            value={`${props.data.vehicle.licenceNumber} - ${props.data.vehicle.name}`}
            isLong={isLongVehicle}
            name={`vehicle-${props.data.vehicle.id}`}
            newStyle
          />
          {props.data && (
            <Label
              title={
                props.type === IS_USAGE
                  ? f({ id: 'tripsOverview.modal.rideStart' })
                  : f({ id: 'tripsOverview.modal.parkingStart' })
              }
              value={`${getStartTimestamp()}
                      - ${getStartAddress()}`}
              newStyle
              isLong={isLongStart}
              name={`ride-start-${props.data.timestamp.start}`}
            />
          )}
          {props.data.timestamp.end && (
            <Label
              title={
                props.type === IS_USAGE
                  ? f({ id: 'tripsOverview.modal.rideEnd' })
                  : f({ id: 'tripsOverview.modal.parkingEnd' })
              }
              value={getEndValue()}
              newStyle
              isLong={isLongEnd}
              name={`ride-end-${props.data.timestamp.end}`}
            />
          )}
        </div>
      )}
      <Controller
        name="tagId"
        control={control}
        rules={{
          required: errors.tagId ? errors.tagId.message : undefined,
        }}
        render={({ field }) => (
          <CmdChipSelect
            {...field}
            name="tagId"
            id="tagId"
            label={
              props.type === 'usage'
                ? f({ id: 'tripsOverview.modal.rideUsage' })
                : f({ id: 'tripsOverview.modal.parkingUsage' })
            }
            options={JSON.parse(JSON.stringify(options))}
            placeholder={f({ id: 'form.choose' })}
            searchPlaceholder={f({ id: 'form.search' })}
            selectButton={f({ id: 'form.groupChipSelectUnique.button.apply' })}
            resetButton={f({ id: 'form.groupChipSelectUnique.button.reset' })}
            required={true}
            searchTooltip={f({
              id: 'alarms.section.vehicles.driverId.search.tooltip',
            })}
            value={watch('tagId')}
            selectButtonTooltip={f({
              id: 'rides.modal.usage.button.select.tooltip',
            })}
            resetButtonTooltip={f({
              id: 'rides.modal.usage.button.reset.tooltip',
            })}
            setValue={(value) => {
              setValue('tagId', value);
              trigger('tagId');
            }}
            error={errors.tagId?.message}
            touched={true}
          />
        )}
      />
    </CmdModal>
  );
}

export default AddUsage;
